import { useState } from 'react';
import type { DialogProps as UndercurrentDialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useAssetRemove from 'modules/queries/asset/useRemove';
import acronisDetailsAlsowith from 'containers/pages/servers/details/acronisDetailsAlsowith';
import LWForm from 'components/LWForm';
import {
	ADDON_FORM_VALIDATOR,
	INITIAL_FORM_ERRORS,
	INITIAL_FORM_VALUES,
} from '../DialogDestroyAddon/constant';
import AddonCard from '../DialogDestroyAddon/AddonCard';
import AddonWrapper from '../DialogDestroyAddon/AddonWrapper';
import DestroyButton from '../DialogDestroyAddon/DestroyButton';
import DialogDestroyAddon from '../DialogDestroyAddon';
import DestroyNote from '../DialogDestroyAddon/DestroyNote';
import ReasonList from '../DialogDestroyAddon/ReasonList';

export type DialogDestroyAcronisProps = Omit<
	UndercurrentDialogProps,
	'onClose'
> & {
	uniqId: string;
	onClose: () => void;
};

const DialogDestroyAcronis: React.FC<DialogDestroyAcronisProps> = ({
	uniqId,
	onClose,
	...dialogProps
}) => {
	const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
	const [formErrors, setFormErrors] = useState(INITIAL_FORM_ERRORS);

	const { data: acronisAssetDetails, isLoading: isAcronisAssetDetailsLoading } =
		useAssetDetails(
			{
				alsowith: acronisDetailsAlsowith,
				uniq_id: uniqId,
			},
			{ enabled: !!uniqId },
		);
	const { mutate: removeAsset, isLoading } = useAssetRemove();

	const hostnames =
		acronisAssetDetails?.acronisBackingUp
			?.map((item: { domain: string }) => item.domain)
			?.join('') ?? '';

	const handleSubmit = (values: typeof INITIAL_FORM_VALUES) => {
		removeAsset(
			{
				uniq_id: uniqId,
				cancellation_reason: String(values.cancellation_reason),
				cancellation_comment:
					String(values.additional_info).length > 0
						? String(values.additional_info)
						: undefined,
				cleanup_assets: 0,
			},
			{ onSuccess: () => handleClose() },
		);
	};

	const handleClose = () => {
		setFormValues(INITIAL_FORM_VALUES);
		setFormErrors(INITIAL_FORM_ERRORS);
		onClose();
	};

	return (
		<DialogDestroyAddon
			title="Canceling your service? Tell us a bit more"
			subtitle="We want to make sure your cancellation is handled quickly and correctly. Let us know why you’re canceling so we can provide the proper cancellation experience."
			onClose={handleClose}
			{...dialogProps}
		>
			<LWForm
				values={formValues}
				onValuesChange={setFormValues}
				errors={formErrors}
				validators={ADDON_FORM_VALIDATOR}
				onErrorsChange={setFormErrors}
				onSubmit={handleSubmit}
			>
				<Stack gap={400}>
					<Stack gap={400}>
						<ReasonList />
						<AddonWrapper>
							<AddonCard
								title={`Acronis Backups: ${!isAcronisAssetDetailsLoading ? hostnames : ''}`}
								addOnType="Acronis"
							/>
						</AddonWrapper>
						<DestroyNote>
							Removal may take a few moments, or a few days for dedicated
							servers.
						</DestroyNote>
					</Stack>
					<Stack alignItems="center">
						<DestroyButton loading={isLoading} />
					</Stack>
				</Stack>
			</LWForm>
		</DialogDestroyAddon>
	);
};

export default DialogDestroyAcronis;
